import { Globe, Users, Building2, Lock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import type { AssetSharingStatus } from '@/services/asset-service';

interface AssetSharingIndicatorsProps {
  status: AssetSharingStatus;
}

/**
 * Displays simple sharing status indicators for an asset
 * Shows icons for: public, shared with users, shared with org, or private
 * All icons use same color for minimal visual noise
 * Uses lightweight status object (boolean flags) instead of full sharing details
 */
export function AssetSharingIndicators({ status }: AssetSharingIndicatorsProps) {
  const { isPublic, hasUserShares, hasOrgShares, hasTeamShares } = status;
  const isPrivate = !isPublic && !hasUserShares && !hasOrgShares && !hasTeamShares;

  if (isPrivate) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Lock className="h-4 w-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>Private - Only you</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1.5">
        {isPublic && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Globe className="h-4 w-4 text-foreground" />
            </TooltipTrigger>
            <TooltipContent>Public</TooltipContent>
          </Tooltip>
        )}

        {hasUserShares && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Users className="h-4 w-4 text-foreground" />
            </TooltipTrigger>
            <TooltipContent>Shared with users</TooltipContent>
          </Tooltip>
        )}

        {hasOrgShares && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Building2 className="h-4 w-4 text-foreground" />
            </TooltipTrigger>
            <TooltipContent>Shared with organization</TooltipContent>
          </Tooltip>
        )}

        {hasTeamShares && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Users className="h-4 w-4 text-foreground" />
            </TooltipTrigger>
            <TooltipContent>Shared with team</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
